import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { Button, Card, SectionTitle, Skeleton, TextArea, TextField } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { PhotoEditor } from '../../components/PhotoEditor';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { toForm } from '../../lib/media';

export interface DriverMe {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  professionalPhone: string | null;
  bio: string | null;
  experienceYears: number | null;
  presence: 'AVAILABLE' | 'BUSY' | 'OFFLINE';
  isPublic: boolean;
  photoUrl: string | null;
  rating: { average: string; count: number };
  services: Array<{ id: string; name: string; code: string }>;
  trips: number;
  vehicle: { make: string; model: string; year: number; color: string; type: string; plate: string; photoUrl: string | null } | null;
}
interface Service {
  id: string;
  name: string;
}

interface FormValues {
  firstName: string;
  lastName: string;
  professionalPhone: string;
  bio: string;
  experienceYears: string;
  isPublic: boolean;
  serviceTypeIds: string[];
}

export const useDriverMe = () => useQuery({ queryKey: ['driver-me'], queryFn: () => api<DriverMe>('/drivers/me') });

export function DriverProfilePage() {
  const qc = useQueryClient();
  const toast = useToast();
  const me = useDriverMe();
  const services = useQuery({ queryKey: ['services'], queryFn: () => api<Service[]>('/services') });
  const form = useForm<FormValues>();

  useEffect(() => {
    const d = me.data;
    if (d)
      form.reset({
        firstName: d.firstName,
        lastName: d.lastName,
        professionalPhone: d.professionalPhone ?? '',
        bio: d.bio ?? '',
        experienceYears: d.experienceYears?.toString() ?? '',
        isPublic: d.isPublic,
        serviceTypeIds: d.services.map((s) => s.id),
      });
  }, [me.data, form]);

  const save = form.handleSubmit(async (v) => {
    try {
      await api('/users/me', { method: 'PATCH', body: { firstName: v.firstName, lastName: v.lastName } });
      await api('/drivers/me', {
        method: 'PATCH',
        body: {
          bio: v.bio,
          professionalPhone: v.professionalPhone || undefined,
          experienceYears: v.experienceYears ? Number(v.experienceYears) : undefined,
          isPublic: v.isPublic,
          serviceTypeIds: v.serviceTypeIds,
        },
      });
      await qc.invalidateQueries({ queryKey: ['driver-me'] });
      toast('Perfil guardado', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  });

  const d = me.data;
  return (
    <Page title="Perfil profesional" back="/driver/more">
      <div className="flex flex-col gap-6">
        <Card className="p-5">
          {!d ? (
            <Skeleton className="h-24" />
          ) : (
            <>
              <PhotoEditor
                src={d.photoUrl}
                firstName={d.firstName}
                lastName={d.lastName}
                onUpload={async (blob) => (await api('/drivers/me/photo', { method: 'PUT', form: toForm(blob, {}, 'conductor') }), qc.invalidateQueries({ queryKey: ['driver-me'] }))}
              />
              <p className="mt-4 text-sm text-[var(--text-muted)] flex items-center gap-1">
                <Star className="size-4 fill-amber-400 text-amber-400" aria-hidden />
                {d.rating.count ? `${d.rating.average} de 5 · ${d.rating.count} calificaciones` : 'Aún sin calificaciones'}
              </p>
            </>
          )}
          <p className="mt-2 text-xs text-[var(--text-muted)]">Usa una foto profesional: los clientes la ven antes de subir al vehículo.</p>
        </Card>

        <form onSubmit={save} className="flex flex-col gap-6">
          <Card className="p-5 flex flex-col gap-4">
            <SectionTitle title="Presentación" />
            <div className="grid grid-cols-2 gap-3">
              <TextField label="Nombre" {...form.register('firstName', { required: true })} />
              <TextField label="Apellido" {...form.register('lastName', { required: true })} />
            </div>
            <TextField label="Teléfono profesional" type="tel" hint="Se muestra a tus clientes con viajes confirmados" {...form.register('professionalPhone')} />
            <TextField label="Años de experiencia" type="number" min={0} max={60} {...form.register('experienceYears')} />
            <TextArea label="Descripción" maxLength={1000} hint="Aparece en la página pública" {...form.register('bio')} />
            <label className="tap flex items-center gap-3 text-sm">
              <input type="checkbox" className="size-5 accent-[#E5252A]" {...form.register('isPublic')} />
              Mostrar mi perfil en la página pública
            </label>
          </Card>

          <Card className="p-5">
            <SectionTitle title="Servicios que ofreces" />
            <div className="grid sm:grid-cols-2 gap-2">
              {services.data?.map((s) => (
                <label key={s.id} className="tap flex items-center gap-3 rounded-xl border border-[var(--border)] px-3 py-2 text-sm">
                  <input type="checkbox" value={s.id} className="size-5 accent-[#E5252A]" {...form.register('serviceTypeIds')} />
                  {s.name}
                </label>
              ))}
            </div>
          </Card>

          <Button type="submit" size="lg" loading={form.formState.isSubmitting}>Guardar perfil</Button>
        </form>
      </div>
    </Page>
  );
}
